import { useState, useEffect, useCallback, useMemo, FC } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Card, Stack, Heading } from '@leroy-merlin-br/backyard-react'
import cookies from 'js-cookie'
import axios from 'axios'

import { BalanceList } from 'lmcv/components'
import { getProfileBalance } from 'lmcv/services/loyaltyProfile'
import { showErrorAlert } from 'lmcv/utils/showErrorAlert'
import { PRO_EAD_ACTIVE } from 'lmcv/utils/LoyaltyStatus/constants'

import { Points } from './components/Points'
import { TimeLine } from './components/TimeLine'
import * as S from './styled'

function safeToDate(value) {
  if (!value) {
    return null
  }

  const d = new Date(value)

  if (Number.isNaN(d.getTime())) {
    return null
  }

  return d
}

function getDateFromAny(item) {
  if (!item) {
    return null
  }

  const raw = item.date || item.eventDate

  return safeToDate(raw)
}

function mapLysHistoryToBalanceItem(item) {
  const dt = getDateFromAny(item)

  const attrs = (item && item.attributes) || {}

  const pointsNum = Number(attrs.value)
  const safePoints = Number.isFinite(pointsNum) ? Math.abs(pointsNum) : 0

  const op = String(attrs.operation || item.operation || '').toUpperCase()
  const transaction_type = op === 'BURN' ? 'debit' : 'credit'

  const title =
    attrs.kindLabel ||
    (transaction_type === 'debit'
      ? 'Resgate de pontos (LYS)'
      : 'Crédito de pontos (LYS)')

  const actionCode = String(item.actionCode || '').toUpperCase()
  const kind = String(attrs.kind || '').toUpperCase()

  let transaction_icon = 'stars'

  if (transaction_type === 'debit') {
    transaction_icon = 'coupon_money'
  } else {
    if (kind === 'TRN' || actionCode.indexOf('EARN') >= 0) {
      transaction_icon = 'cart_special'
    } else if (actionCode.indexOf('REGULARIZE') >= 0) {
      transaction_icon = 'stars'
    } else {
      transaction_icon = 'cart'
    }
  }

  let id =
    item.id ||
    item.historyId ||
    item.uuid ||
    item.transactionId ||
    item.reference

  if (!id) {
    const ts = dt ? String(dt.getTime()) : 'no-date'
    id =
      String(transaction_type) +
      '-' +
      ts +
      '-' +
      String(safePoints) +
      '-' +
      String(title)
  }

  return {
    id: String(id),
    title,
    transaction_type,
    transaction_icon,
    points: safePoints,
    date: dt ? dt.toISOString() : null,

    raw: item,
    source: 'LYS'
  }
}

type MyPointsPageProps = {
  mdmClientNumber: number
  lysHomepageEnabled: boolean
}

const MyPointsPage: FC<MyPointsPageProps> = ({
  mdmClientNumber,
  lysHomepageEnabled
}) => {
  const [balanceLegacy, setBalanceLegacy] = useState([])
  const [isLoadingLegacy, setIsLoadingLegacy] = useState(false)
  const [hasNextPageLegacy, setHasNextPageLegacy] = useState(true)
  const [page, setPage] = useState(0)

  const isLysEnabled = lysHomepageEnabled && mdmClientNumber

  const [lysPoints, setLysPoints] = useState(function () {
    return { isLys: false, actual: 0, expireAt: null }
  })
  const [isLoadingLysPoints, setIsLoadingLysPoints] = useState(false)

  const [lysHistory, setLysHistory] = useState([])
  const [isLoadingLysHistory, setIsLoadingLysHistory] = useState(false)

  const selector = useSelector(function (state) {
    return state.loyaltyProfile.get
  }, shallowEqual)

  const loyaltyProfile = selector.payload
  const isRequest = selector.isRequest

  const fetchBalanceLegacy = async function (pageProp) {
    setIsLoadingLegacy(true)

    try {
      const res = await getProfileBalance(pageProp)
      const data = res.data
      const meta = res.meta

      if (!meta || !meta.next_page) {
        setHasNextPageLegacy(false)
      }

      setBalanceLegacy(function (prev) {
        return prev.concat(data || [])
      })
    } catch (e) {
      showErrorAlert('points')
    }

    setIsLoadingLegacy(false)
  }

  const fetchLysPoints = useCallback(
    async function () {
      if (!isLysEnabled) {
        return
      }

      setIsLoadingLysPoints(true)

      try {
        const token = cookies.get('api_token')

        if (!token) {
          setLysPoints({ isLys: false, actual: 0, expireAt: null })
          return
        }

        const response = await axios.get(
          '/api/v2/loyaltyProgram/lys/loyalty-accounts',
          {
            params: {
              customer: String(mdmClientNumber)
            },
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )

        const payload = response.data

        const account = payload.data.data

        if (account && Array.isArray(account.counters)) {
          const pointsCounter = account.counters.find(function (counter) {
            return counter && counter.type && counter.type.value === 'P'
          })

          setLysPoints({
            isLys: true,
            actual:
              pointsCounter && pointsCounter.value ? pointsCounter.value : 0,
            expireAt:
              pointsCounter && pointsCounter.expirationDate
                ? pointsCounter.expirationDate
                : null
          })
        } else {
          setLysPoints({ isLys: false, actual: 0, expireAt: null })
        }
      } catch (err) {
        setLysPoints({ isLys: false, actual: 0, expireAt: null })
      } finally {
        setIsLoadingLysPoints(false)
      }
    },
    [isLysEnabled, mdmClientNumber]
  )

  const fetchLysHistory = useCallback(
    async function () {
      if (!isLysEnabled) return

      setIsLoadingLysHistory(true)

      try {
        const token = cookies.get('api_token')

        if (!token) {
          setLysHistory([])

          return
        }

        const response = await axios.get(
          '/api/v2/loyaltyProgram/lys/histories/points',
          {
            params: {
              customer: String(mdmClientNumber),
              page: 0,
              rowsPerPage: 50,
              sortDirection: 'DESC'
            },
            withCredentials: true,
            headers: {
              accept: 'application/json, text/plain, */*',
              Authorization: 'Bearer ' + token
            }
          }
        )

        const payload = response.data
        const histories = payload?.data?.data?.histories || []

        if (!Array.isArray(histories)) {
          setLysHistory([])
          return
        }

        setLysHistory(histories)
      } catch (err) {
        setLysHistory([])
      } finally {
        setIsLoadingLysHistory(false)
      }
    },
    [isLysEnabled, mdmClientNumber]
  )

  const handleLoadMore = useCallback(
    function () {
      if (isLysEnabled) {
        return
      }

      setPage(function (currentPage) {
        return currentPage + 1
      })
    },
    [isLysEnabled]
  )

  useEffect(
    function () {
      if (isLysEnabled) {
        return
      }

      setPage(1)
    },
    [isLysEnabled]
  )

  useEffect(
    function () {
      if (page && !isLysEnabled) {
        fetchBalanceLegacy(page)
      }
    },
    [page, isLysEnabled]
  )

  useEffect(
    function () {
      if (!isLysEnabled) {
        return
      }

      fetchLysPoints()
      fetchLysHistory()
    },
    [isLysEnabled, fetchLysPoints, fetchLysHistory]
  )

  useEffect(
    function () {
      if (!isLysEnabled) {
        return
      }

      const onUpdated = function () {
        fetchLysPoints()
        fetchLysHistory()
      }

      window.addEventListener('lys:points-updated', onUpdated)
      return function () {
        window.removeEventListener('lys:points-updated', onUpdated)
      }
    },
    [isLysEnabled, fetchLysPoints, fetchLysHistory]
  )

  const pointsUser = useMemo(
    function () {
      if (!loyaltyProfile) {
        return loyaltyProfile
      }

      if (!isLysEnabled) {
        return loyaltyProfile
      }

      const basePoints =
        loyaltyProfile.points && typeof loyaltyProfile.points === 'object'
          ? loyaltyProfile.points
          : { actual: 0, season: { expireAt: null, toExpire: 0 } }

      const baseSeason =
        basePoints.season && typeof basePoints.season === 'object'
          ? basePoints.season
          : { expireAt: null, toExpire: 0 }

      return {
        ...loyaltyProfile,
        points: {
          ...basePoints,
          actual: Number.isFinite(lysPoints.actual) ? lysPoints.actual : 0,
          season: {
            ...baseSeason,
            expireAt: lysPoints.expireAt || null,
            toExpire: 0
          }
        }
      }
    },
    [loyaltyProfile, isLysEnabled, lysPoints]
  )

  const mergedBalance = useMemo(
    function () {
      if (isLysEnabled) {
        const lysMapped = Array.isArray(lysHistory)
          ? lysHistory.map(mapLysHistoryToBalanceItem)
          : []

        lysMapped.sort(function (a, b) {
          const da = getDateFromAny(a)
          const db = getDateFromAny(b)

          const ta = da ? da.getTime() : 0
          const tb = db ? db.getTime() : 0

          return tb - ta
        })

        return lysMapped
      }

      const legacy = Array.isArray(balanceLegacy) ? balanceLegacy : []
      return legacy
    },
    [balanceLegacy, isLysEnabled, lysHistory]
  )

  const isBalanceLoading = isLysEnabled ? isLoadingLysHistory : isLoadingLegacy
  const isPointsLoading = isLysEnabled ? isLoadingLysPoints : isRequest
  const hasNextPage = isLysEnabled ? false : hasNextPageLegacy

  if (isRequest) {
    return null
  }

  if (loyaltyProfile.status === PRO_EAD_ACTIVE) {
    return (
      <Stack>
        <Card>
          <Heading size="giga">Meus Pontos</Heading>
          <Points user={pointsUser} isLoading={isPointsLoading} isLoyaltyPro />
        </Card>

        {!lysPoints.isLys && (
          <TimeLine isLoading={isRequest} data={loyaltyProfile} />
        )}

        <S.Card>
          <Heading size="giga">Saldo de Pontos</Heading>
          <div>
            <BalanceList
              loadMore={handleLoadMore}
              data={mergedBalance}
              isLoading={isBalanceLoading}
              hasNextPage={hasNextPage}
            />
          </div>
        </S.Card>
      </Stack>
    )
  }

  return (
    <S.Card>
      <Heading size="giga">Historico</Heading>
      <Points user={pointsUser} isLoading={isPointsLoading} />
      <div>
        <BalanceList
          loadMore={handleLoadMore}
          data={mergedBalance}
          isLoading={isBalanceLoading}
          hasNextPage={hasNextPage}
        />
      </div>
    </S.Card>
  )
}

export default MyPointsPage
